<template>
    <b-modal
        id="contract-detail-modal"
        title="기타요금 관리"
        size="xl"
        hide-footer
        modal-class="ebc-settings-dialog"
        @hide="onModalHide"
        @show="onModalShow"
    >
        <!-- 계약 정보 요약 -->
        <div class="contract-info-section">
            <div class="info-grid">
                <div class="info-item">
                    <span class="info-label">계약명:</span>
                    <span class="info-value">{{ contract.contractName || "-" }}</span>
                </div>
                <div class="info-item">
                    <span class="info-label">공급자:</span>
                    <span class="info-value">{{ contract.supplier?.supplierName || "-" }}</span>
                </div>
                <div class="info-item">
                    <span class="info-label">요금제:</span>
                    <span class="info-value">{{ contract.cost?.costName || "-" }}</span>
                </div>
            </div>
        </div>

        <!-- 기타요금 목록 테이블 -->
        <div class="details-list-section">
            <div class="section-header">
                <h5>기타요금 목록</h5>
                <button class="btn-action btn-primary" @click="openCreateForm">
                    <i class="fas fa-plus"></i>
                    신규 등록
                </button>
            </div>

            <!-- 목록 테이블 -->
            <div class="details-table-container">
                <table class="details-table">
                    <thead>
                        <tr>
                            <th>항목명</th>
                            <th>계산 방식</th>
                            <!-- <th class="text-right">비율/금액</th> -->
                            <th>적용 기간</th>
                            <th class="text-center">상태</th>
                            <th class="text-center">작업</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-if="contractDetailsList.length === 0">
                            <td colspan="5" class="text-center no-data">등록된 기타요금이 없습니다.</td>
                        </tr>
                        <tr v-for="detail in contractDetailsList" :key="detail.detailIdx">
                            <td>{{ detail.etcCostName }}</td>
                            <!-- <td>
                                <span class="calc-type-badge" :class="detail.calcType.toLowerCase()">
                                    {{ getCalcTypeName(detail.calcType) }}
                                </span>
                            </td> -->
                            <td>{{ formatPrice(detail) }}</td>
                            <td>{{ formatPeriod(detail) }}</td>
                            <td class="text-center">
                                <span :class="['status-badge', detail.enabled === 'Y' ? 'active' : 'inactive']">
                                    {{ detail.enabled === "Y" ? "활성" : "비활성" }}
                                </span>
                            </td>
                            <td class="text-center actions-cell">
                                <button class="btn-icon-edit" @click="openEditForm(detail)" title="수정">
                                    <i class="fas fa-edit"></i>
                                </button>
                                <button class="btn-icon-delete" @click="handleDelete(detail)" title="삭제">
                                    <i class="fas fa-trash-alt"></i>
                                </button>
                                <button class="btn-icon-price" @click="openPriceChangeForm(detail)" title="단가 변경">
                                    <i class="fas fa-dollar-sign"></i>
                                </button>
                                <button class="btn-icon-history" @click="openHistoryModal(detail)" title="이력 조회">
                                    <i class="fas fa-history"></i>
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <!-- 등록/수정 모달 -->
        <b-modal
            modal-class="ebc-settings-dialog"
            id="detail-form-modal"
            :title="isEditMode ? '기타요금 수정' : '기타요금 등록'"
            size="md"
            hide-footer
            @hide="closeDetailForm"
        >
            <div class="detail-form-container">
                <div class="form-container">
                    <div class="form-row">
                        <div class="form-group">
                            <label> 항목명 <span class="required">*</span> </label>
                            <input
                                type="text"
                                v-model="detailForm.etcCostName"
                                class="form-input"
                                placeholder="기타요금 항목명 입력"
                            />
                        </div>
                        <div class="form-group">
                            <label> 계산 방식 <span class="required">*</span> </label>
                            <select v-model="detailForm.calcType" class="form-select" @change="onCalcTypeChange">
                                <option value="FIXED">고정금액</option>
                                <option value="USAGE">사용량</option>
                                <option value="TOTALCOST">사용금액</option>
                            </select>
                        </div>
                    </div>

                    <div class="form-row">
                        <div class="form-group">
                            <label>
                                <template v-if="detailForm.calcType === 'FIXED'">고정금액</template>
                                <template v-else-if="detailForm.calcType === 'USAGE'">원</template>
                                <template v-else-if="detailForm.calcType === 'TOTALCOST'">비율</template>
                                <span class="required">*</span>
                            </label>
                            <input
                                v-if="detailForm.calcType === 'USAGE'"
                                type="number"
                                v-model.number="detailForm.unitPrice"
                                class="form-input"
                                step="1"
                                min="0"
                                placeholder="0"
                            />
                            <input
                                v-else-if="detailForm.calcType === 'FIXED'"
                                type="number"
                                v-model.number="detailForm.fixedAmount"
                                class="form-input"
                                step="1"
                                min="0"
                                placeholder="0"
                            />
                            <input
                                v-else-if="detailForm.calcType === 'TOTALCOST'"
                                type="number"
                                v-model.number="detailForm.rate"
                                class="form-input"
                                step="0.1"
                                min="0"
                                placeholder="0"
                            />
                        </div>
                        <div class="form-group">
                            <label> 정렬 순서 </label>
                            <input
                                type="number"
                                v-model.number="detailForm.sortOrder"
                                class="form-input"
                                min="0"
                                placeholder="0"
                            />
                        </div>
                    </div>

                    <div class="form-row">
                        <div class="form-group">
                            <label> 적용 시작일 <span class="required">*</span> </label>
                            <input type="date" v-model="detailForm.applyStartDt" class="form-input" />
                        </div>
                        <div class="form-group">
                            <label> 적용 종료일 </label>
                            <input type="date" v-model="detailForm.applyEndDt" class="form-input" />
                            <small class="help-text">미입력 시 계속 진행</small>
                        </div>
                    </div>

                    <div class="form-row">
                        <div class="form-group">
                            <label> 상태 <span class="required">*</span> </label>
                            <div class="radio-group">
                                <label class="radio-label">
                                    <input type="radio" v-model="detailForm.enabled" value="Y" />
                                    <span>활성</span>
                                </label>
                                <label class="radio-label">
                                    <input type="radio" v-model="detailForm.enabled" value="N" />
                                    <span>비활성</span>
                                </label>
                            </div>
                        </div>
                    </div>

                    <div class="form-actions">
                        <button class="btn-cancel" @click="closeDetailForm">
                            <i class="fas fa-times"></i>
                            취소
                        </button>
                        <button class="btn-save" @click="handleSaveDetail">
                            <i class="fas fa-save"></i>
                            저장
                        </button>
                    </div>
                </div>
            </div>
        </b-modal>

        <!-- 단가 변경 모달 -->
        <b-modal
            modal-class="ebc-settings-dialog"
            id="price-change-modal"
            :title="`단가 변경 - ${editingDetail?.etcCostName || ''}`"
            size="md"
            hide-footer
            @hide="closePriceChangeForm"
        >
            <div v-if="editingDetail" class="price-change-form">
                <div class="current-price-info">
                    <div class="info-item">
                        <span class="info-label">현재 {{ getCalcTypeName(editingDetail.calcType) }}:</span>
                        <span class="info-value">{{ formatPrice(editingDetail) }}</span>
                    </div>
                    <div class="info-item">
                        <span class="info-label">현재 적용 시작일:</span>
                        <span class="info-value">{{
                            editingDetail.applyStartDt ? editingDetail.applyStartDt.substring(0, 10) : "-"
                        }}</span>
                    </div>
                </div>

                <div class="form-group">
                    <label>
                        새로운 {{ getCalcTypeName(editingDetail.calcType) }}
                        <span class="required">*</span>
                    </label>
                    <input
                        v-if="editingDetail.calcType === 'USAGE'"
                        type="number"
                        v-model.number="priceChangeForm.unitPrice"
                        class="form-input"
                        step="0.0001"
                        placeholder="0.0000"
                    />
                    <input
                        v-else
                        type="number"
                        v-model.number="priceChangeForm.fixedAmount"
                        class="form-input"
                        step="1"
                        placeholder="0"
                    />
                    <small class="help-text" v-if="editingDetail.calcType === 'USAGE'">
                        예: 0.0037 (전력기금), 0.0050 (기후환경요금)
                    </small>
                    <small class="help-text" v-else> 예: 5000, 42312 </small>
                </div>

                <div class="form-group">
                    <label> 새로운 적용 시작일 <span class="required">*</span> </label>
                    <input type="date" v-model="priceChangeForm.newApplyStartDt" class="form-input" />
                    <small class="help-text">변경된 단가가 적용될 시작일을 입력해주세요.</small>
                </div>

                <div class="form-actions">
                    <button class="btn-cancel" @click="closePriceChangeForm">
                        <i class="fas fa-times"></i>
                        취소
                    </button>
                    <button class="btn-save" @click="handlePriceChange">
                        <i class="fas fa-save"></i>
                        변경
                    </button>
                </div>
            </div>
        </b-modal>

        <!-- 이력 조회 모달 -->
        <b-modal
            modal-class="ebc-settings-dialog"
            id="history-modal"
            :title="`단가 변경 이력 - ${editingDetail?.etcCostName || ''}`"
            hide-footer
            @hide="closeHistoryModal"
        >
            <div v-if="priceChangeHistory.length === 0" class="no-data">
                <i class="fas fa-inbox"></i>
                <p>변경 이력이 없습니다.</p>
            </div>
            <div v-else class="history-table-container">
                <table class="history-table">
                    <thead>
                        <tr>
                            <th>적용 기간</th>
                            <th>계산 방식</th>
                            <th class="text-right">비율/금액/사용금액</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(history, index) in priceChangeHistory" :key="history.detailIdx || index">
                            <td>{{ formatPeriod(history) }}</td>
                            <td>
                                <span class="calc-type-badge" :class="history.calcType?.toLowerCase()">
                                    {{ getCalcTypeName(history.calcType) }}
                                </span>
                            </td>
                            <td class="text-right">{{ formatPrice(history) }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </b-modal>
    </b-modal>
</template>

<script>
import backEndApi from "@src/api/backEndApi.js";

export default {
    name: "ContractDetailModal",
    data() {
        return {
            // 선택된 계약 정보
            contract: {
                contractIdx: null,
                contractName: "",
                ptIdx: null,
                supplier: null,
                cost: null,
            },
            // 기타요금 목록
            contractDetailsList: [],
            // 등록/수정 모드
            isEditMode: false,
            // 수정 중인 항목
            editingDetail: null,
            // 기타요금 폼 데이터
            detailForm: {
                detailIdx: null,
                contractIdx: null,
                etcCostName: "",
                calcType: "USAGE",
                unitPrice: null,
                fixedAmount: null,
                rate: null,
                applyStartDt: "",
                applyEndDt: null,
                sortOrder: 0,
                enabled: "Y",
            },
            // 단가 변경 관련
            priceChangeForm: {
                detailIdx: null,
                detailType: "",
                unitPrice: null,
                fixedAmount: null,
                newApplyStartDt: "",
            },
            // 이력 조회
            priceChangeHistory: [],
        };
    },
    methods: {
        // 모달 열기 (부모 컴포넌트에서 호출)
        open(contract) {
            this.contract = { ...contract };
            this.resetData();
            this.$bvModal.show("contract-detail-modal");
        },
        // 모달 표시 시
        onModalShow() {
            if (this.contract.contractIdx) {
                this.loadContractDetailsList();
            }
        },
        // 모달 닫힐 때
        onModalHide() {
            this.resetData();
        },
        // 데이터 초기화
        resetData() {
            this.contractDetailsList = [];
            this.isEditMode = false;
            this.editingDetail = null;
            this.resetDetailForm();
            this.priceChangeHistory = [];
        },
        // 폼 데이터 초기화
        resetDetailForm() {
            this.detailForm = {
                detailIdx: null,
                contractIdx: null,
                etcCostName: "",
                calcType: "USAGE",
                unitPrice: null,
                fixedAmount: null,
                rate: null,
                applyStartDt: "",
                applyEndDt: null,
                sortOrder: 0,
                enabled: "Y",
            };
        },
        // 기타요금 목록 조회
        async loadContractDetailsList() {
            try {
                const result = await backEndApi.energyBillCalc.getContractDetailsList({
                    contractIdx: this.contract.contractIdx,
                });
                if (result.status === 200) {
                    // sortOrder 기준 정렬
                    this.contractDetailsList = (result.data || []).sort((a, b) => {
                        return (a.sortOrder || 0) - (b.sortOrder || 0);
                    });
                } else {
                    this.$notify({
                        type: "warning",
                        title: "경고",
                        text: result.data?.message || "기타요금 목록 조회에 실패했습니다.",
                    });
                }
            } catch (error) {
                console.error("기타요금 목록 조회 실패:", error);
                this.$notify({
                    type: "error",
                    title: "오류",
                    text: "기타요금 목록을 불러오는데 실패했습니다.",
                });
            }
        },
        // 계산 방식 이름 변환
        getCalcTypeName(calcType) {
            const typeMap = {
                FIXED: "고정금액",
                USAGE: "사용량",
                TOTALCOST: "사용금액",
            };
            return typeMap[calcType] || calcType;
        },
        // 가격 포맷팅
        formatPrice(detail) {
            if (!detail) return "-";
            if (detail.calcType === "USAGE") {
                // 요율 표시 (소수점 4자리) - unitPrice 사용
                return `${this.getCalcTypeName(detail.calcType)} X ${parseFloat(detail.unitPrice || 0)}원`;
            } else if (detail.calcType === "FIXED") {
                // 금액 표시 (천단위 구분) - fixedAmount 사용
                return `고정금액(${this.formatNumber(parseFloat(detail.fixedAmount || 0))}원)`;
            } else if (detail.calcType === "TOTALCOST") {
                // 사용금액 표시 (천단위 구분) - rate 사용
                return `${this.getCalcTypeName(detail.calcType)} X ${this.formatNumber(parseFloat(detail.rate || 0))}%`;
            }
            return "-";
        },
        // 적용 기간 포맷팅
        formatPeriod(detail) {
            if (!detail) return "-";
            const start = detail.applyStartDt ? detail.applyStartDt.substring(0, 10) : "-";
            const end = detail.applyEndDt ? detail.applyEndDt.substring(0, 10) : "진행중";
            return `${start} ~ ${end}`;
        },
        // 숫자 포맷팅 (천단위 구분)
        formatNumber(value) {
            return new Intl.NumberFormat("ko-KR").format(value || 0);
        },
        // 계산 방식 변경 시 처리
        async onCalcTypeChange() {
            // TOTALCOST 선택 시 기존 TOTALCOST 항목 확인
            if (this.detailForm.calcType === "TOTALCOST") {
                // 이미 등록된 TOTALCOST 항목이 있는지 확인 (수정 모드에서는 현재 항목 제외)
                const existingTotalCost = this.contractDetailsList.find(
                    (detail) =>
                        detail.calcType === "TOTALCOST" &&
                        (!this.isEditMode || detail.detailIdx !== this.detailForm.detailIdx),
                );

                if (existingTotalCost) {
                    await this.alertWarning(
                        `이미 계산 방식이 "사용금액(TOTALCOST)"인 기타요금이 등록되어 있습니다.\n항목명: ${existingTotalCost.etcCostName}`,
                    );
                    // 기존 값으로 되돌리기 (이전 calcType으로 복원)
                    if (this.isEditMode && this.editingDetail) {
                        this.detailForm.calcType = this.editingDetail.calcType || "USAGE";
                    } else {
                        this.detailForm.calcType = "USAGE";
                    }
                    // 되돌린 calcType에 맞게 필드 초기화
                    if (this.detailForm.calcType === "USAGE") {
                        this.detailForm.fixedAmount = null;
                        this.detailForm.rate = null;
                    } else if (this.detailForm.calcType === "FIXED") {
                        this.detailForm.unitPrice = null;
                        this.detailForm.rate = null;
                    }
                    return;
                }
            }

            // 계산 방식 변경 시 반대 필드 초기화
            if (this.detailForm.calcType === "USAGE") {
                this.detailForm.fixedAmount = null;
                this.detailForm.rate = null;
            } else if (this.detailForm.calcType === "FIXED") {
                this.detailForm.unitPrice = null;
                this.detailForm.rate = null;
            } else if (this.detailForm.calcType === "TOTALCOST") {
                this.detailForm.unitPrice = null;
                this.detailForm.fixedAmount = null;
            } else {
                this.detailForm.unitPrice = null;
            }
        },
        // 신규 등록 폼 열기
        openCreateForm() {
            this.isEditMode = false;
            this.editingDetail = null;
            this.resetDetailForm();
            this.detailForm.contractIdx = this.contract.contractIdx;
            this.$bvModal.show("detail-form-modal");
        },
        // 수정 폼 열기
        openEditForm(detail) {
            this.isEditMode = true;
            this.editingDetail = { ...detail };
            this.detailForm = {
                detailIdx: detail.detailIdx,
                contractIdx: detail.contractIdx,
                etcCostName: detail.etcCostName || "",
                calcType: detail.calcType || "USAGE",
                unitPrice: detail.calcType === "USAGE" && detail.unitPrice ? parseFloat(detail.unitPrice) : null,
                fixedAmount: detail.calcType === "FIXED" && detail.fixedAmount ? parseFloat(detail.fixedAmount) : null,
                rate: detail.calcType === "TOTALCOST" && detail.rate ? parseFloat(detail.rate) : null,
                applyStartDt: detail.applyStartDt ? detail.applyStartDt.substring(0, 10) : "",
                applyEndDt: detail.applyEndDt ? detail.applyEndDt.substring(0, 10) : null,
                sortOrder: detail.sortOrder || 0,
                enabled: detail.enabled || "Y",
            };
            this.$bvModal.show("detail-form-modal");
        },
        // 폼 닫기
        closeDetailForm() {
            this.isEditMode = false;
            this.editingDetail = null;
            this.resetDetailForm();
            this.$bvModal.hide("detail-form-modal");
        },
        // 저장 처리
        async handleSaveDetail() {
            // 검증
            if (!this.detailForm.etcCostName || !this.detailForm.calcType || !this.detailForm.applyStartDt) {
                await this.alertWarning("필수 항목을 모두 입력해주세요.");
                return;
            }

            // calcType에 따른 값 검증
            if (this.detailForm.calcType === "USAGE") {
                if (
                    this.detailForm.unitPrice === null ||
                    this.detailForm.unitPrice === undefined ||
                    this.detailForm.unitPrice === ""
                ) {
                    await this.alertWarning("요율을 입력해주세요.");
                    return;
                }
            } else if (this.detailForm.calcType === "FIXED") {
                if (
                    this.detailForm.fixedAmount === null ||
                    this.detailForm.fixedAmount === undefined ||
                    this.detailForm.fixedAmount === ""
                ) {
                    await this.alertWarning("금액을 입력해주세요.");
                    return;
                }
            } else if (this.detailForm.calcType === "TOTALCOST") {
                if (
                    this.detailForm.rate === null ||
                    this.detailForm.rate === undefined ||
                    this.detailForm.rate === ""
                ) {
                    await this.alertWarning("사용금액을 입력해주세요.");
                    return;
                }
            }

            try {
                // payload 구성 - calcType에 따라 unitPrice 또는 fixedAmount 사용
                const payload = {
                    contractIdx: this.contract.contractIdx,
                    etcCostName: this.detailForm.etcCostName,
                    calcType: this.detailForm.calcType,
                    unitPrice: this.detailForm.calcType === "USAGE" ? Number(this.detailForm.unitPrice) : null, // USAGE일 때만, number 타입
                    fixedAmount: this.detailForm.calcType === "FIXED" ? Number(this.detailForm.fixedAmount) : null, // FIXED일 때만, number 타입
                    rate: this.detailForm.calcType === "TOTALCOST" ? Number(this.detailForm.rate) : null, // TOTALCOST일 때만, number 타입
                    applyStartDt: this.detailForm.applyStartDt,
                    applyEndDt: this.detailForm.applyEndDt || null,
                    sortOrder: this.detailForm.sortOrder || 0,
                    enabled: this.detailForm.enabled,
                    contract: {
                        contractIdx: this.contract.contractIdx,
                        contractName: this.contract.contractName,
                        ptIdx: this.contract.ptIdx,
                    },
                };

                let result;
                if (this.isEditMode) {
                    payload.detailIdx = this.detailForm.detailIdx;
                    const confirm = await this.alertConfirmWarning("기타요금 정보를 수정하시겠습니까?");
                    if (!confirm.value) return;

                    result = await backEndApi.energyBillCalc.updateContractDetails(payload);
                } else {
                    const confirm = await this.alertConfirmWarning("새로운 기타요금을 등록하시겠습니까?");
                    if (!confirm.value) return;

                    result = await backEndApi.energyBillCalc.insertContractDetails(payload);
                }

                if (result.status === 200) {
                    await this.alertNoti(
                        this.isEditMode ? "기타요금 정보가 수정되었습니다." : "새로운 기타요금이 등록되었습니다.",
                    );
                    this.closeDetailForm();
                    await this.loadContractDetailsList();
                } else {
                    await this.alertWarning(result.data?.message || "저장에 실패했습니다.");
                }
            } catch (error) {
                console.error("기타요금 저장 실패:", error);
                await this.alertWarning("기타요금 정보를 저장하는데 실패했습니다.");
            }
        },
        // 삭제 처리
        async handleDelete(detail) {
            const confirm = await this.alertConfirmWarning(`'${detail.etcCostName}' 항목을 삭제하시겠습니까?`);
            if (!confirm.value) return;

            try {
                const result = await backEndApi.energyBillCalc.deleteContractDetails({
                    contractIdx: detail.contractIdx,
                    detailIdx: detail.detailIdx,
                });

                if (result.status === 200) {
                    await this.alertNoti("삭제되었습니다.");
                    await this.loadContractDetailsList();
                } else {
                    await this.alertWarning(result.data?.message || "삭제에 실패했습니다.");
                }
            } catch (error) {
                console.error("기타요금 삭제 실패:", error);
                await this.alertWarning("기타요금 삭제에 실패했습니다.");
            }
        },
        // 단가 변경 폼 열기
        openPriceChangeForm(detail) {
            this.editingDetail = { ...detail };
            this.priceChangeForm = {
                detailIdx: detail.detailIdx,
                detailType: detail.calcType,
                unitPrice: detail.calcType === "USAGE" && detail.unitPrice ? parseFloat(detail.unitPrice) : null,
                fixedAmount: detail.calcType === "FIXED" && detail.fixedAmount ? parseFloat(detail.fixedAmount) : null,
                newApplyStartDt: "",
            };
            this.$bvModal.show("price-change-modal");
        },
        // 단가 변경 폼 닫기
        closePriceChangeForm() {
            this.priceChangeForm = {
                detailIdx: null,
                detailType: "",
                unitPrice: null,
                fixedAmount: null,
                newApplyStartDt: "",
            };
            this.$bvModal.hide("price-change-modal");
        },
        // 단가 변경 처리
        async handlePriceChange() {
            // 필수 필드 검증
            if (!this.priceChangeForm.newApplyStartDt) {
                await this.alertWarning("새로운 적용 시작일을 입력해주세요.");
                return;
            }

            // calcType에 따른 검증
            if (this.priceChangeForm.detailType === "USAGE") {
                if (
                    this.priceChangeForm.unitPrice === null ||
                    this.priceChangeForm.unitPrice === undefined ||
                    this.priceChangeForm.unitPrice === ""
                ) {
                    await this.alertWarning("요율을 입력해주세요.");
                    return;
                }
            } else if (this.priceChangeForm.detailType === "FIXED") {
                if (
                    this.priceChangeForm.fixedAmount === null ||
                    this.priceChangeForm.fixedAmount === undefined ||
                    this.priceChangeForm.fixedAmount === ""
                ) {
                    await this.alertWarning("금액을 입력해주세요.");
                    return;
                }
            }

            try {
                const confirm = await this.alertConfirmWarning("단가를 변경하시겠습니까?");
                if (!confirm.value) return;

                // payload 구성 - 예시: { detailIdx, newApplyStartDt, unitPrice 또는 fixedAmount }
                const payload = {
                    detailIdx: this.priceChangeForm.detailIdx,
                    newApplyStartDt: this.priceChangeForm.newApplyStartDt,
                };

                // calcType에 따라 unitPrice 또는 fixedAmount 사용
                if (this.priceChangeForm.detailType === "USAGE") {
                    payload.unitPrice = Number(this.priceChangeForm.unitPrice); // number 타입
                } else if (this.priceChangeForm.detailType === "FIXED") {
                    payload.fixedAmount = Number(this.priceChangeForm.fixedAmount); // number 타입
                }

                const result = await backEndApi.energyBillCalc.changeContractDetailPrice(payload);

                if (result.status === 200) {
                    await this.alertNoti("단가가 변경되었습니다.");
                    this.closePriceChangeForm();
                    await this.loadContractDetailsList();
                } else {
                    await this.alertWarning(result.data?.message || "단가 변경에 실패했습니다.");
                }
            } catch (error) {
                console.error("단가 변경 실패:", error);
                await this.alertWarning("단가 변경에 실패했습니다.");
            }
        },
        // 이력 조회 모달 열기
        async openHistoryModal(detail) {
            this.editingDetail = { ...detail };
            try {
                const result = await backEndApi.energyBillCalc.getContractDetailsHistory({
                    detailIdx: detail.detailIdx,
                });

                if (result.status === 200) {
                    // 응답 데이터는 배열 형태로 기타요금 상세 정보 전체를 포함
                    this.priceChangeHistory = result.data || [];
                    // applyStartDt 기준 내림차순 정렬 (최신순)
                    this.priceChangeHistory.sort((a, b) => {
                        const dateA = new Date(a.applyStartDt || 0);
                        const dateB = new Date(b.applyStartDt || 0);
                        return dateB - dateA;
                    });
                    this.$bvModal.show("history-modal");
                } else {
                    await this.alertWarning(result.data?.message || "이력 조회에 실패했습니다.");
                }
            } catch (error) {
                console.error("이력 조회 실패:", error);
                await this.alertWarning("이력 조회에 실패했습니다.");
            }
        },
        // 이력 조회 모달 닫기
        closeHistoryModal() {
            this.priceChangeHistory = [];
            this.$bvModal.hide("history-modal");
        },
    },
};
</script>

<style scoped>
/* 계약 정보 섹션 */
.contract-info-section {
    margin-bottom: 24px;
    padding: 16px;
    background: var(--ebc-surface-sunken);
    border-radius: var(--ebc-radius-sm);
    border: 1px solid var(--ebc-border);
}

.info-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
}

.info-item {
    display: flex;
    align-items: center;
    gap: 5px;
}

.info-label {
    font-size: 12px;
    font-weight: 600;
    color: var(--ebc-text-sub);
}

.info-value {
    font-size: 14px;
    font-weight: 600;
    color: var(--ebc-text);
}

/* 섹션 헤더 */
.section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
}

.section-header h5 {
    font-size: 16px;
    font-weight: 700;
    color: var(--ebc-text);
    margin: 0;
}

/* 테이블 */
.details-table-container {
    border: 1px solid var(--ebc-hairline);
    border-radius: var(--ebc-radius-md);
    overflow: hidden;
}

.details-table {
    width: 100%;
    border-collapse: collapse;
}

.details-table thead {
    background: var(--ebc-surface-sunken);
}

.details-table th {
    padding: 12px 16px;
    text-align: left;
    font-size: 12px;
    font-weight: 700;
    color: var(--ebc-text-sub);
    border-bottom: 1px solid var(--ebc-hairline);
}

.details-table tbody tr {
    border-bottom: 1px solid var(--ebc-track);
    transition: all 0.2s;
}

.details-table tbody tr:hover {
    background: var(--ebc-surface-sunken);
}

.details-table td {
    padding: 14px 16px;
    font-size: 13px;
    color: var(--ebc-text);
}

.text-center {
    text-align: center !important;
}

.text-right {
    text-align: right !important;
}

.no-data {
    padding: 40px;
    text-align: center;
    color: var(--ebc-text-muted);
}

.no-data i {
    font-size: 28px;
    margin-bottom: 12px;
    opacity: 0.5;
}

/* 배지 */
.calc-type-badge {
    display: inline-block;
    padding: 4px 12px;
    border-radius: var(--ebc-radius-xs);
    font-size: 11px;
    font-weight: 600;
}

.calc-type-badge.usage,
.calc-type-badge.fixed {
    background: var(--ebc-track);
    color: var(--ebc-text);
}

.status-badge {
    display: inline-block;
    padding: 4px 12px;
    border-radius: var(--ebc-radius-xs);
    font-size: 12px;
    font-weight: 600;
}

.status-badge.active {
    background: var(--ebc-positive-tint);
    color: var(--ebc-positive);
}

.status-badge.inactive {
    background: var(--ebc-danger-tint);
    color: var(--ebc-danger);
}

/* 액션 버튼 */
.actions-cell {
    white-space: nowrap;
}

.btn-icon-edit,
.btn-icon-delete,
.btn-icon-price,
.btn-icon-history {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    height: var(--ebc-btn-h-sm);
    padding: 0 10px;
    min-width: 44px;
    font-size: 11px;
    line-height: 1;
    white-space: nowrap;
    box-sizing: border-box;
    border: 1px solid var(--ebc-border);
    border-radius: var(--ebc-radius-xs);
    background: white;
    cursor: pointer;
    transition: all 0.2s;
    margin-right: 4px;
}

.btn-icon-edit,
.btn-icon-price,
.btn-icon-history {
    color: var(--ebc-text);
    border-color: var(--ebc-border-strong);
}

.btn-icon-edit:hover,
.btn-icon-price:hover,
.btn-icon-history:hover {
    background: var(--ebc-track);
    border-color: var(--ebc-text-muted);
}

.btn-icon-delete {
    color: var(--ebc-danger);
    border-color: var(--ebc-danger);
}

.btn-icon-delete:hover {
    background: var(--ebc-danger-tint);
}

/* 폼 컨테이너 */
.detail-form-container {
    padding: 20px;
    border: 1px solid var(--ebc-hairline);
    border-radius: var(--ebc-radius-md);
    background: white;
}

.detail-form-container h6 {
    font-size: 16px;
    font-weight: 700;
    color: var(--ebc-text);
    margin-bottom: 20px;
    padding-bottom: 12px;
    border-bottom: 1px solid var(--ebc-hairline);
}

.form-container {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
}

.form-group {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.form-group label {
    font-size: 13px;
    font-weight: 600;
    color: var(--ebc-text);
}

.required {
    color: var(--ebc-danger);
}

.form-input,
.form-select {
    padding: 10px 12px;
    border: 1px solid var(--ebc-hairline-strong);
    border-radius: var(--ebc-radius-sm);
    font-size: 13px;
    transition: all 0.2s;
}

.form-input:focus,
.form-select:focus {
    outline: none;
    border-color: var(--ebc-primary);
    box-shadow: 0 0 0 3px var(--ebc-primary-tint);
}

.help-text {
    font-size: 11px;
    color: var(--ebc-text-muted);
}

.radio-group {
    display: flex;
    gap: 12px;
    padding: 8px 0;
}

.radio-label {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    font-size: 13px;
    padding: 8px 16px;
    border: 2px solid var(--ebc-hairline-strong);
    border-radius: var(--ebc-radius-sm);
    background: var(--ebc-surface);
    transition: all 0.2s;
}

.radio-label:hover {
    border-color: var(--ebc-primary);
    background: var(--ebc-warn-tint);
}

.radio-label input[type="radio"] {
    cursor: pointer;
    accent-color: var(--ebc-primary);
}

.radio-label:has(input[type="radio"]:checked) {
    border-color: var(--ebc-primary);
    background: var(--ebc-warn-tint);
    color: var(--ebc-primary);
}

.form-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding-top: 16px;
    border-top: 1px solid var(--ebc-border);
}

.btn-action {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    height: var(--ebc-btn-h-md);
    padding: 0 14px;
    min-width: 64px;
    font-size: 12px;
    line-height: 1;
    white-space: nowrap;
    box-sizing: border-box;
    border: 1px solid var(--ebc-border);
    border-radius: var(--ebc-radius-xs);
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-primary {
    background: var(--ebc-primary);
    color: var(--ebc-surface);
    border: 1px solid var(--ebc-primary);
}

.btn-primary:hover {
    background: var(--ebc-primary-hover);
    border-color: var(--ebc-primary-hover);
    color: var(--ebc-surface);
}

.btn-secondary {
    background: white;
    color: var(--ebc-text-sub);
    border-color: var(--ebc-border);
}

.btn-secondary:hover {
    background: var(--ebc-track);
}

.btn-cancel,
.btn-save {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    height: var(--ebc-btn-h-md);
    padding: 0 14px;
    min-width: 64px;
    font-size: 12px;
    line-height: 1;
    white-space: nowrap;
    box-sizing: border-box;
    border: 1px solid var(--ebc-border);
    border-radius: var(--ebc-radius-xs);
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-cancel {
    background: white;
    color: var(--ebc-text-sub);
}

.btn-cancel:hover {
    background: var(--ebc-track);
}

.btn-save {
    background: var(--ebc-primary);
    color: var(--ebc-surface);
    border: 1px solid var(--ebc-primary);
}

.btn-save:hover {
    background: var(--ebc-primary-hover);
    border-color: var(--ebc-primary-hover);
    color: var(--ebc-surface);
}

/* 단가 변경 폼 */
.price-change-form {
    padding: 20px 0;
}

.current-price-info {
    padding: 16px;
    background: var(--ebc-surface-sunken);
    border-radius: var(--ebc-radius-sm);
    margin-bottom: 20px;
}

/* 이력 테이블 */
.history-table-container {
    border: 1px solid var(--ebc-hairline);
    border-radius: var(--ebc-radius-md);
    overflow: hidden;
}

.history-table {
    width: 100%;
    border-collapse: collapse;
}

.history-table thead {
    background: var(--ebc-surface-sunken);
}

.history-table th {
    padding: 12px 16px;
    text-align: left;
    font-size: 12px;
    font-weight: 700;
    color: var(--ebc-text-sub);
    border-bottom: 1px solid var(--ebc-hairline);
}

.history-table tbody tr {
    border-bottom: 1px solid var(--ebc-track);
    transition: all 0.2s;
}

.history-table tbody tr:hover {
    background: var(--ebc-surface-sunken);
}

.history-table td {
    padding: 14px 16px;
    font-size: 13px;
    color: var(--ebc-text);
}

.history-detail-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.detail-item {
    font-size: 11px;
    color: var(--ebc-text-muted);
    font-family: monospace;
}
</style>
